// CustomerContext.js
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { redirect } from 'react-router';
import PropTypes from 'prop-types'; // @mui
import { apiService } from 'src/utils/apiServices';
import { toast } from 'react-toastify';
import { useSettingsContext } from 'src/components/settings';
import { GZAVNILI_MODES } from 'src/components/settings/drawer/gzavnili-mode';
import { PHYSICAL_API, emptyToNull, withoutEmptyValues } from 'src/utils/physical-api';

const CustomerContext = createContext();

const toSenderPayload = (customer) =>
  withoutEmptyValues({
    first_name: customer.first_name,
    last_name: customer.last_name,
    first_name_ka: customer.first_name_ka,
    last_name_ka: customer.last_name_ka,
    id_number: customer.id_number,
    main_phone_number: customer.phone,
    email: customer.email,
    is_resident: customer.isNotResident === undefined ? undefined : !customer.isNotResident,
  });

const fetchSenders = async (filters) => {
  const { data } = await apiService.get(PHYSICAL_API.senders, {
    params: withoutEmptyValues({ name: filters.name, page: filters.page, take: filters.take }),
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
  });
  return { customers: data.data, page: data.page, take: data.take, total: data.total };
};
const CustomerActionsContext = createContext();

export const CustomerProvider = ({ children }) => {
  const [customers, setCustomers] = useState([]);
  const [custumerRecordDetails, setCustomerRecordDetails] = useState({});

  const { gzavniliMode } = useSettingsContext();
  const isPhysicalRef = useRef(false);
  isPhysicalRef.current = gzavniliMode === GZAVNILI_MODES.physical;

  const createCustomer = async (newCustomer, addressData) => {
    try {
      const isPhysical = isPhysicalRef.current;
      const requestConfig = isPhysical
        ? {
            timeout: 20000,
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        : {
            headers: {
              timeout: 20000,
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          };
      const response = await apiService.post(
        isPhysical ? PHYSICAL_API.senders : '/customers',
        isPhysical ? toSenderPayload(newCustomer) : newCustomer,
        requestConfig
      );
      // eslint-disable-next-line
      const data = response.data;
      setCustomers((prevCustomers) => [...prevCustomers, data]);
      toast.success('Customer created!');

      const dataAddressResponse = await apiService.post(
        isPhysical ? PHYSICAL_API.addresses : '/personal-address',
        isPhysical
          ? { ...withoutEmptyValues(addressData), customer_id: data.id }
          : { ...addressData, customer_id: data.id },
        requestConfig
      );

      // Log the full response and the data
      if (dataAddressResponse) {
        getCustomers();
      }
    } catch (error) {
      const msg = error?.response?.data?.message;
      toast.error(msg ? `Error creating customer: ${[].concat(msg).join(', ')}` : 'Error creating customer!');
      console.error('Error creating customer:', error);
    }
  };

  const deleteCustomer = async (customerId) => {
    try {
      await apiService.put(
        '/customers/update/soft',
        { ids: customerId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setCustomers((prevCustomers) =>
        prevCustomers.filter((customer) => !customerId.includes(customer.id))
      );
      toast.success('customer deleted!');
    } catch (error) {
      toast.error('Error deleting customer');
      console.error('Error deleting customer:', error);
    }
  };

  const updateCustomer = async (customerId, updatedCustomer) => {
    try {
      // Simulate API call to update customer
      const isPhysical = isPhysicalRef.current;
      const response = await apiService.put(
        isPhysical
          ? `${PHYSICAL_API.senders}/${customerId}`
          : `/customers/updateUserFromDashboard/${customerId}`,
        isPhysical ? toSenderPayload(updatedCustomer) : updatedCustomer,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const data = await response.data;
      setCustomers((prevCustomers) =>
        prevCustomers.map((customer) =>
          customer.id === customerId ? { ...customer, ...data } : customer
        )
      );
      toast.success('customer updated!');
    } catch (error) {
      toast.error('Error updating customer');
      console.error('Error updating customer:', error);
    }
  };

  const updatePersonalAddress = async (addressId, updatedAddress) => {
    try {
      // Simulate API call to update customer
      const isPhysical = isPhysicalRef.current;
      const response = await apiService.patch(
        isPhysical ? `${PHYSICAL_API.addresses}/${addressId}` : `/personal-address/${addressId}`,
        isPhysical ? emptyToNull(updatedAddress) : updatedAddress,
        {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      // eslint-disable-next-line
      const data = await response.data;
      toast.success('address updated!');
    } catch (error) {
      toast.error('Error updating address');
      console.error('Error updating address:', error);
    }
  };

  const getCustomers = async (filters = {}) => {
    if (isPhysicalRef.current) {
      try {
        const senders = await fetchSenders(filters);
        setCustomers(senders.customers);
        setCustomerRecordDetails({
          page: senders.page,
          take: senders.take,
          total: senders.total,
          totalPages: Math.ceil(+senders.total / +senders.take),
        });
      } catch (error) {
        console.error('Error fetching senders:', error);
        if (error?.response?.status === 401) {
          localStorage.removeItem('token');
          redirect('/');
        }
      }
      return;
    }
    try {
      const effectiveFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (key === 'role' && Array.isArray(value) && value.length === 0) {
          return acc; // skip adding to the accumulator
        }
        acc[key] = value; // otherwise add to the accumulator
        return acc;
      }, {});

      const query = new URLSearchParams(effectiveFilters).toString();
      // Simulate API call to get customers
      const response = await apiService.get(`/customers?${query}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.data.customers;
      setCustomers(data);
      setCustomerRecordDetails({
        page: response.data.page,
        take: response.data.take,
        total: response.data.total,
        totalPages: Math.ceil(+response.data.total / +response.data.take),
      });
    } catch (error) {
      console.error('Error fetching customers:', error);
      if (error.response.status === 401) {
        localStorage.removeItem('token');
        redirect('/');
      }
    }
  };
  const getPhysicalCustomers = async (filters = {}) => {
    try {
      return await fetchSenders(filters);
    } catch (error) {
      console.error('Error fetching customers:', error);
      if (error?.response?.status === 401) {
        localStorage.removeItem('token');
        redirect('/');
      }
      // safe fallback
      return { customers: [], page: 1, take: 0, total: 0 };
    }
  };

  const getSenderById = async (senderId) => {
    const response = await apiService.get(`${PHYSICAL_API.senders}/${senderId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  };

  const getCustomerById = async (customerId) => {
    try {
      const url = isPhysicalRef.current
        ? `${PHYSICAL_API.senders}/${customerId}`
        : `/customers/${customerId}`;
      const response = await apiService.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching customer by ID:', error);
      throw error;
    }
  };

  const deleteCard = async (customerId, cardId) => {
    try {
      const response = await apiService.post(
        `/customers/deleteCard`,
        { id: customerId, cardId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      toast.success('Card deleted successfully');
      return response.data;
    } catch (error) {
      toast.error(error.response.data.error);
      throw error;
    }
  };

  const getCustomerCards = async (id) => {
    try {
      // Simulate API call to get customer by ID
      const response = await apiService.get(`/customers/findCards/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching customer by ID:', error);
      throw error;
    }
  };
  useEffect(() => {
    // getCustomers();
  }, []);

  const createCustomerCallback = useCallback(createCustomer, []);
  const updatePersonalAddressCallback = useCallback(updatePersonalAddress, []);
  const deleteCustomerCallback = useCallback(deleteCustomer, []);
  const updateCustomerCallback = useCallback(updateCustomer, [updateCustomer]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getCustomersCallback = useCallback(getCustomers, [gzavniliMode]);
  const getCustomerByIdCallback = useCallback(getCustomerById, []);
  const getCustomerCardsCallback = useCallback(getCustomerCards, []);
  const deleteCardCallback = useCallback(deleteCard, []);
  const getPhysicalCustomersCallback = useCallback(getPhysicalCustomers, []);
  const getSenderByIdCallback = useCallback(getSenderById, []);
  const customerActionsValue = useMemo(
    () => ({
      createCustomer: createCustomerCallback,
      deleteCustomer: deleteCustomerCallback,
      updateCustomer: updateCustomerCallback,
      getCustomers: getCustomersCallback,
      getCustomerById: getCustomerByIdCallback,
      getCustomerCards: getCustomerCardsCallback,
      deleteCard: deleteCardCallback,
      updatePersonalAddress: updatePersonalAddressCallback,
      getPhysicalCustomers: getPhysicalCustomersCallback,
      getSenderById: getSenderByIdCallback,
    }),
    [
      createCustomerCallback,
      deleteCustomerCallback,
      updateCustomerCallback,
      getCustomersCallback,
      getCustomerByIdCallback,
      getCustomerCardsCallback,
      deleteCardCallback,
      updatePersonalAddressCallback,
      getPhysicalCustomersCallback,
      getSenderByIdCallback,
    ]
  );

  const customersValue = useMemo(
    () => ({
      customers,
      custumerRecordDetails,
    }),
    [customers, custumerRecordDetails]
  );

  return (
    <CustomerContext.Provider value={customersValue}>
      <CustomerActionsContext.Provider value={customerActionsValue}>
        {children}
      </CustomerActionsContext.Provider>
    </CustomerContext.Provider>
  );
};

export const useCustomerData = () => useContext(CustomerContext);
export const useCustomerActions = () => useContext(CustomerActionsContext);

CustomerProvider.propTypes = {
  children: PropTypes.element,
};
